import { withCategoryStanding } from '../models/podium.models';
import { Injectable, inject } from '@angular/core';
import { DogService } from './dog.service';
import { HumanService } from './human.service';
import { ResultService } from './result.service';
import { HumanProfileData, HumanProfileResult, MyProfileData } from '../models/human-profile.models';
import { collapseKnownDogDuplicates } from '../models/dog-identity.models';

@Injectable({ providedIn: 'root' })
export class HumanProfileDataService {
  private readonly humanService = inject(HumanService);
  private readonly resultService = inject(ResultService);
  private readonly dogService = inject(DogService);

  private readonly humanCache = new Map<string, HumanProfileData>();
  private readonly myProfileCache = new Map<string, MyProfileData>();
  private readonly humanRequests = new Map<string, Promise<HumanProfileData>>();
  private readonly myProfileRequests = new Map<string, Promise<MyProfileData>>();

  getCachedHumanProfile(humanId: string): HumanProfileData | undefined {
    return this.humanCache.get(humanId);
  }

  getCachedMyProfile(userId: string): MyProfileData | undefined {
    return this.myProfileCache.get(userId);
  }

  async getHumanProfile(humanId: string, forceRefresh = false): Promise<HumanProfileData> {
    if (!forceRefresh) {
      const cached = this.humanCache.get(humanId);
      if (cached) return cached;
    }

    const runningRequest = this.humanRequests.get(humanId);
    if (runningRequest) return runningRequest;

    const request = this.fetchHumanProfile(humanId).then(data => {
      this.humanCache.set(humanId, data);
      return data;
    }).finally(() => {
      this.humanRequests.delete(humanId);
    });

    this.humanRequests.set(humanId, request);
    return request;
  }

  async getMyProfile(userId: string, forceRefresh = false): Promise<MyProfileData> {
    if (!forceRefresh) {
      const cached = this.myProfileCache.get(userId);
      if (cached) return cached;
    }

    const runningRequest = this.myProfileRequests.get(userId);
    if (runningRequest) return runningRequest;

    const request = this.fetchMyProfile(userId).then(data => {
      this.myProfileCache.set(userId, data);
      return data;
    }).finally(() => {
      this.myProfileRequests.delete(userId);
    });

    this.myProfileRequests.set(userId, request);
    return request;
  }

  invalidate(userId?: string, humanId?: string): void {
    if (userId) this.myProfileCache.delete(userId);
    if (humanId) this.humanCache.delete(humanId);
  }

  private async fetchHumanProfile(humanId: string, knownHuman?: Awaited<ReturnType<HumanService['getHuman']>>): Promise<HumanProfileData> {
    const [human, rawResults] = await Promise.all([
      knownHuman === undefined ? this.humanService.getHuman(humanId) : Promise.resolve(knownHuman),
      this.resultService.getResultsByHuman(humanId),
    ]);
    const results = collapseKnownDogDuplicates(rawResults);

    if (!human) {
      return {
        human: null,
        results: [],
        participantCounts: {},
        categoryParticipantCounts: {},
      };
    }

    const raceIds = [...new Set(results.map(result => result.raceId))];
    const [participantCounts, categoryStandings] = await Promise.all([
      this.resultService.getRaceParticipantCounts(raceIds),
      this.resultService.getCategoryStandings(raceIds),
    ]);

    const mappedResults = [...results]
      .sort((a, b) => new Date(b.event?.date || 0).getTime() - new Date(a.event?.date || 0).getTime())
      .map(result => {
        const categoryRank = withCategoryStanding(result, categoryStandings[result.id]).categoryRank;
        return { ...result, categoryRank, rank: categoryRank } as HumanProfileResult;
      });

    return {
      human,
      results: mappedResults,
      participantCounts,
      categoryParticipantCounts: Object.fromEntries(
        results.map(result => [result.id, categoryStandings[result.id]?.participantCount ?? 0]),
      ),
    };
  }

  private async fetchMyProfile(userId: string): Promise<MyProfileData> {
    const human = await this.humanService.findHumanByProfileId(userId);
    if (!human) {
      return {
        human: null,
        results: [],
        participantCounts: {},
        categoryParticipantCounts: {},
        dogs: [],
      };
    }

    const [profileData, dogs] = await Promise.all([
      this.fetchHumanProfile(human.id, human).then(data => {
        this.humanCache.set(human.id, data);
        return data;
      }),
      this.dogService.getDogsByOwner(human.id),
    ]);

    return { ...profileData, human, dogs };
  }
}
